import React from 'react'
import type { TypedDataSigner } from '@ethersproject/abstract-signer'
import { BigNumber, Signer } from 'ethers'
import { useTranslation } from 'next-i18next'
import { faXmark, faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAddress, useChainId, useSetIsWalletModalOpen, useSigner, useSwitchChain } from '@thirdweb-dev/react'
import { TrophyDetailComponentDto } from '~/modules/Trophies/Infrastructure/TrophyDetailComponentDto'
import requestMint from '~/modules/Trophies/RequestMint/Application/RequestMint'
import { Minted } from './Minted'
import { Minting } from './Minting'
import styles from './index.module.scss'

interface MintButtonProps {
  trophy: TrophyDetailComponentDto
}

export const MintButton: React.FC<MintButtonProps> = ({ trophy }) => {
  const { t } = useTranslation('trophy_details')
  const setIsWalletModalOpen = useSetIsWalletModalOpen()
  const address = useAddress()
  const signer = useSigner()
  const chainId = useChainId()
  const switchChain = useSwitchChain()
  const connected = address !== undefined && signer !== undefined && chainId !== undefined
  const [isMinted, setIsMinted] = React.useState<boolean>(false)
  const [isMinting, setIsMinting] = React.useState<boolean>(false)
  const [isError, setIsError] = React.useState<boolean>(false)

  const handleClick = async () => {
    setIsError(false)

    if (!connected) {
      setIsWalletModalOpen(true)

      return
    }

    const targetChainId = trophy.collection?.contractChain
      ? parseInt(trophy.collection.contractChain)
      : undefined

    if (!targetChainId) return

    if (targetChainId !== chainId) {
      switchChain(targetChainId)

      return
    }

    setIsMinting(true)
    try {
      await requestMint(
        signer as Signer & TypedDataSigner,
        trophy.collection?.contractChain
          ? parseInt(trophy.collection.contractChain)
          : chainId,
        BigNumber.from(trophy.id),
        address
      )
      setIsMinted(true)
    } catch (exception: unknown) {
      setIsError(true)
      console.log(exception)
    } finally {
      setIsMinting(false)
    }
  }

  const normalIcon = (
    <FontAwesomeIcon
      className={ styles.mintButton__title__icon }
      icon={ faWandMagicSparkles }
      size='lg'
    />
  )

  const errorIcon = (
    <FontAwesomeIcon
      className={ `
        ${styles.mintButton__title__icon}
        ${styles.mintButton__title__icon__error}
      ` }
      icon={ faXmark }
      size='lg'
    />
  )

  return (
    <>
      { isMinting && <Minting/> }

      { isMinted && <Minted/> }

      {
        !isMinted && !isMinting &&
          <button
            className={ styles.mintButton__button }
            onClick={ handleClick }
            disabled={ isMinting }
          >
            <div className={ styles.mintButton__title }>
              <p>
                { t('mint_title') }
              </p>

              {
                isError
                  ? errorIcon
                  : normalIcon
              }
            </div>

            <p className={ styles.mintButton__subtitle }>
              { t('mint_subtitle') }
            </p>
          </button>
      }
    </>
  )
}
